import { Index, Show } from 'solid-js';
import Text from '../Text';
import { useTranslation } from '../../hooks/useTranslation';
import { gradientPaint, noPaint, solidPaint, type Paint, type PaintKind } from '../../mapart/document';
import styles from './PaintEditor.module.css';

const round2 = (value: number) => Math.round(value * 100) / 100;

// No shared colour control exists in the repo; this stays local to the mapart panels and only
// composes global inputs plus btn-* buttons.
interface Props {
    label: string;
    paint: Paint;
    onChange: (next: Paint) => void;
    onCommit: () => void;
}

export default function PaintEditor(props: Props) {
    const { t } = useTranslation();

    const setKind = (kind: PaintKind) => {
        const current = props.paint;
        if (kind === 'none') props.onChange(noPaint());
        else if (kind === 'solid') props.onChange(solidPaint(current.stops[0]?.colour ?? current.colour));
        else {
            const from = current.stops[0]?.colour ?? current.colour;
            const to = current.stops[current.stops.length - 1]?.colour ?? '#000000';
            props.onChange({ ...gradientPaint(kind, from, to), angle: current.angle || 90 });
        }
        props.onCommit();
    };

    const patchStop = (index: number, values: Partial<{ offset: number; colour: string }>) => {
        const stops = props.paint.stops.map((stop, i) => (i === index ? { ...stop, ...values } : stop));
        props.onChange({ ...props.paint, stops });
    };

    const addStop = () => {
        const stops = [...props.paint.stops, { offset: 1, colour: '#000000' }].sort((a, b) => a.offset - b.offset);
        props.onChange({ ...props.paint, stops });
        props.onCommit();
    };

    const removeStop = (index: number) => {
        if (props.paint.stops.length <= 2) return;
        props.onChange({ ...props.paint, stops: props.paint.stops.filter((_, i) => i !== index) });
        props.onCommit();
    };

    const isGradient = () => props.paint.kind === 'linear' || props.paint.kind === 'radial';

    return (
        <div class={styles.block}>
            <span class={styles.label}>{props.label}</span>

            <select value={props.paint.kind} onChange={(e) => setKind(e.currentTarget.value as PaintKind)}>
                <option value="none">{t('MAPART_PAINT_NONE')}</option>
                <option value="solid">{t('MAPART_PAINT_SOLID')}</option>
                <option value="linear">{t('MAPART_PAINT_LINEAR')}</option>
                <option value="radial">{t('MAPART_PAINT_RADIAL')}</option>
            </select>

            <Show when={props.paint.kind === 'solid'}>
                <input
                    type="color"
                    value={props.paint.colour}
                    onInput={(e) => props.onChange({ ...props.paint, colour: e.currentTarget.value })}
                    onChange={() => props.onCommit()}
                />
            </Show>

            <Show when={isGradient()}>
                <Show when={props.paint.kind === 'linear'}>
                    <label class={styles.field}>
                        <span class={styles.hint}>
                            <Text val="MAPART_PAINT_ANGLE" /> <span class={styles.value}>{Math.round(props.paint.angle)}°</span>
                        </span>
                        <div class={styles.sliderRow}>
                            <input
                                type="range"
                                min="0"
                                max="360"
                                step="1"
                                value={props.paint.angle}
                                onInput={(e) => props.onChange({ ...props.paint, angle: Number(e.currentTarget.value) })}
                                onChange={() => props.onCommit()}
                            />
                            <input
                                type="number"
                                min="0"
                                max="360"
                                step="1"
                                class={styles.exact}
                                value={Math.round(props.paint.angle)}
                                onInput={(e) => {
                                    const next = Number(e.currentTarget.value);
                                    if (Number.isFinite(next)) props.onChange({ ...props.paint, angle: next });
                                }}
                                onChange={() => props.onCommit()}
                            />
                        </div>
                    </label>
                </Show>

                <div class={styles.stops}>
                    {/* Index, not For: editing a stop replaces its object, and keying by reference would
                        rebuild the row, which closes the colour picker the moment you pick something. */}
                    <Index each={props.paint.stops}>{(stop, index) => (
                        <div class={styles.stopRow}>
                            <input
                                type="color"
                                value={stop().colour}
                                onInput={(e) => patchStop(index, { colour: e.currentTarget.value })}
                                onChange={() => props.onCommit()}
                            />
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={stop().offset}
                                onInput={(e) => patchStop(index, { offset: Number(e.currentTarget.value) })}
                                onChange={() => props.onCommit()}
                            />
                            <input
                                type="number"
                                min="0"
                                max="1"
                                step="0.01"
                                class={styles.exact}
                                value={round2(stop().offset)}
                                onInput={(e) => {
                                    const next = Number(e.currentTarget.value);
                                    if (Number.isFinite(next)) patchStop(index, { offset: next });
                                }}
                                onChange={() => props.onCommit()}
                            />
                            <button
                                type="button"
                                class="btn btn-secondary btn-sm btn-icon"
                                title={t('MAPART_PAINT_STOP_REMOVE')}
                                disabled={props.paint.stops.length <= 2}
                                onClick={() => removeStop(index)}
                            >
                                <i class="fa-solid fa-minus" />
                            </button>
                        </div>
                    )}</Index>
                    <button type="button" class="btn btn-secondary btn-sm" onClick={addStop}>
                        <i class="fa-solid fa-plus" /> <Text val="MAPART_PAINT_STOP_ADD" />
                    </button>
                </div>
            </Show>
        </div>
    );
}
